import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonButton,
  IonIcon,
  IonInput,
  IonTextarea,
  IonSpinner,
} from '@ionic/react';
import { arrowBackOutline } from 'ionicons/icons';
import { ImpactStyle } from '@capacitor/haptics';
import toast from 'react-hot-toast';
import api from '../../lib/api';
import { useCapacitorPlatform } from '@shared/hooks/useCapacitorPlatform';
import ProCard from '../../components/pro/ProCard';
import './ProContactSupportPage.css';

interface ContactSupportLocationState {
  defaultEmail?: string;
}

/**
 * The page reads `localStorage.token` on mount to decide which submit
 * endpoint to call and whether the name + email fields are needed.
 * Captured at mount only — a token landing mid-form (or being cleared)
 * shouldn't reshape an in-progress message.
 */
const useAuthSnapshot = () =>
  useMemo(() => Boolean(localStorage.getItem('token')), []);

const ProContactSupportPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('pro');
  const { isNative, applyStatusBar, triggerHaptic } =
    useCapacitorPlatform();
  const isAuthed = useAuthSnapshot();

  const state = location.state as ContactSupportLocationState | null;
  const defaultEmail = state?.defaultEmail ?? '';

  // Status bar matches the rest of the Pro app's purple chrome. ProLoginPage
  // and ProTabLayout each own this for the routes they cover; this page is
  // standalone, so it sets it on mount. Reverts to whatever the next mounted
  // page applies (login or tab-layout) on navigate-back.
  useEffect(() => {
    if (!isNative) return;
    void applyStatusBar({ iconStyle: 'light', backgroundColor: '#4D3161' });
  }, [isNative, applyStatusBar]);

  const schema = useMemo(() => {
    const base = {
      category: z.string().min(1),
      subject: z
        .string()
        .min(1, t('contactSupport.subjectRequired'))
        .max(200, t('contactSupport.subjectTooLong')),
      message: z
        .string()
        .min(1, t('contactSupport.messageRequired'))
        .max(5000, t('contactSupport.messageTooLong')),
    };
    if (isAuthed) {
      return z.object(base);
    }
    return z.object({
      ...base,
      name: z
        .string()
        .min(1, t('contactSupport.nameRequired'))
        .max(100, t('contactSupport.nameTooLong')),
      email: z
        .string()
        .min(1, t('contactSupport.emailRequired'))
        .email(t('contactSupport.emailInvalid')),
    });
  }, [isAuthed, t]);

  type FormValues = {
    category: string;
    subject: string;
    message: string;
    name?: string;
    email?: string;
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    // onTouched matches the iOS keyboard-stepper flow: errors don't show
    // pre-emptively, but once a field has been blurred its validation runs
    // on every keystroke so the user gets immediate feedback while fixing
    // mistakes. Submit stays disabled until required text fields have content.
    mode: 'onTouched',
    defaultValues: {
      category: 'General Feedback',
      subject: '',
      message: '',
      name: '',
      email: defaultEmail,
    },
  });

  const mutation = useMutation(
    async (values: FormValues) => {
      const endpoint = isAuthed ? '/feedback/submit' : '/feedback/submit-public';
      const { data } = await api.post(endpoint, values);
      return data;
    },
    {
      onSuccess: async () => {
        await triggerHaptic(ImpactStyle.Light);
        toast.success(t('contactSupport.successToast'));
        navigate(-1);
      },
      onError: (err: unknown) => {
        const apiError =
          (err as { response?: { data?: { error?: string } } })?.response?.data
            ?.error;
        toast.error(apiError || t('contactSupport.errorGeneric'));
      },
    },
  );

  const onSubmit = (values: FormValues) => mutation.mutate(values);

  const subject = watch('subject');
  const message = watch('message');
  const name = watch('name');
  const email = watch('email');

  const canSubmit = Boolean(
    (message ?? '').trim() &&
      (subject ?? '').trim() &&
      (isAuthed || ((name ?? '').trim() && (email ?? '').trim())),
  );

  const categories: Array<{ value: string; label: string }> = [
    { value: 'Bug Report', label: t('contactSupport.categoryBug') },
    { value: 'Feature Request', label: t('contactSupport.categoryFeature') },
    { value: 'General Feedback', label: t('contactSupport.categoryGeneral') },
    { value: 'Subscription', label: t('contactSupport.categorySubscription') },
    { value: 'Documentation', label: t('contactSupport.categoryDocumentation') },
    { value: 'Other', label: t('contactSupport.categoryOther') },
  ];

  return (
    <IonPage className="pro-contact-support pro-ionic citizen-ionic">
      {/* Status-bar safe-area strip — purple, mirrors ProTabLayout's. */}
      <div
        aria-hidden
        className="fixed top-0 left-0 right-0 z-[9998]"
        style={{
          height: 'env(safe-area-inset-top, 0px)',
          backgroundColor: '#4D3161',
        }}
      />
      <IonHeader>
        <IonToolbar className="pro-static-toolbar">
          <IonButtons slot="start">
            <IonButton
              fill="clear"
              onClick={() => navigate(-1)}
              className="text-white"
              aria-label={t('a11y.back')}
            >
              <IonIcon slot="icon-only" icon={arrowBackOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle className="text-base font-bold">
            {t('contactSupport.title')}
          </IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="pro-account max-w-3xl mx-auto px-4 pt-4 pb-8"
          noValidate
        >
          <p className="pro-contact-support__intro">
            {isAuthed
              ? t('contactSupport.introAuthed')
              : t('contactSupport.introPublic')}
          </p>

          {!isAuthed && (
            <div className="pro-account__group">
              <p className="pro-account__group-label">
                {t('contactSupport.emailLabel')}
              </p>
              <ProCard>
                <ProField
                  label={t('contactSupport.nameLabel')}
                  error={errors.name?.message}
                >
                  <Controller
                    control={control}
                    name="name"
                    render={({ field }) => (
                      <IonInput
                        className="pro-account__input"
                        fill="outline"
                        autocapitalize="words"
                        autocomplete="name"
                        placeholder={t('contactSupport.namePlaceholder')}
                        value={field.value || ''}
                        onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                        onIonBlur={field.onBlur}
                      />
                    )}
                  />
                </ProField>
                <ProField
                  label={t('contactSupport.emailLabel')}
                  error={errors.email?.message}
                >
                  <Controller
                    control={control}
                    name="email"
                    render={({ field }) => (
                      <IonInput
                        className="pro-account__input"
                        fill="outline"
                        type="email"
                        inputmode="email"
                        autocapitalize="none"
                        autocomplete="email"
                        placeholder={t('contactSupport.emailPlaceholder')}
                        value={field.value || ''}
                        onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                        onIonBlur={field.onBlur}
                      />
                    )}
                  />
                </ProField>
              </ProCard>
            </div>
          )}

          <div className="pro-account__group">
            <p className="pro-account__group-label">
              {t('contactSupport.title')}
            </p>
            <ProCard>
              <ProField label={t('contactSupport.categoryLabel')}>
                <Controller
                  control={control}
                  name="category"
                  render={({ field }) => (
                    /*
                     * Native <select> on purpose. Ionic's IonSelect renders
                     * its options through the same overlay layer (ion-action-
                     * sheet / ion-alert / ion-popover) that this app's
                     * `ion-app { position: relative; contain: none }` override
                     * breaks — see ProPageModal's note. The native control
                     * picks up iOS's wheel picker and Android's native
                     * dropdown, both of which sidestep Ionic's overlay system.
                     */
                    <select
                      className="pro-contact-support__select"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                    >
                      {categories.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </ProField>
              <ProField
                label={t('contactSupport.subjectLabel')}
                error={errors.subject?.message}
              >
                <Controller
                  control={control}
                  name="subject"
                  render={({ field }) => (
                    <IonInput
                      className="pro-account__input"
                      fill="outline"
                      maxlength={200}
                      placeholder={t('contactSupport.subjectPlaceholder')}
                      value={field.value || ''}
                      onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                      onIonBlur={field.onBlur}
                    />
                  )}
                />
              </ProField>
              <ProField
                label={t('contactSupport.messageLabel')}
                error={errors.message?.message}
              >
                <Controller
                  control={control}
                  name="message"
                  render={({ field }) => (
                    <IonTextarea
                      className="pro-account__input"
                      fill="outline"
                      autoGrow
                      rows={6}
                      maxlength={5000}
                      placeholder={t('contactSupport.messagePlaceholder')}
                      value={field.value || ''}
                      onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                      onIonBlur={field.onBlur}
                    />
                  )}
                />
              </ProField>
            </ProCard>
          </div>

          <button
            type="submit"
            disabled={mutation.isLoading || !canSubmit}
            className="pro-contact-support__submit"
          >
            {mutation.isLoading && (
              <IonSpinner name="crescent" className="w-4 h-4" />
            )}
            {mutation.isLoading
              ? t('contactSupport.submitting')
              : t('contactSupport.submit')}
          </button>
        </form>
      </IonContent>
    </IonPage>
  );
};

interface ProFieldProps {
  label: string;
  error?: string;
  children: React.ReactNode;
}

const ProField: React.FC<ProFieldProps> = ({ label, error, children }) => (
  <div className="pro-account__field">
    <span className="pro-account__field-label">{label}</span>
    {children}
    {error && <p className="pro-account__field-error">{error}</p>}
  </div>
);

export default ProContactSupportPage;
